/**
 * Transition to Guest Session
 *
 * AIDEV-NOTE: Issue #567 follow-up. The cleanup half of the SessionExpiredDrawer
 * flow. It clears every gate that would otherwise keep the user stuck in a
 * blocked-recovery state after dismissing the drawer:
 *   - globalSessionExpired flag (read by api-client request interceptor;
 *     when true, every request is hard-rejected client-side with
 *     ERR_SESSION_EXPIRED)
 *   - sessionState (when SESSION_EXPIRED, useAuthNavigation suppresses its
 *     protected-route auto-guard so the recovery flow does not race with it)
 *   - recovery record + circuit-breaker counter (so the next legitimate
 *     refresh failure starts a fresh attempt cycle)
 *
 * After running, the user is a normal guest: API calls go through, public
 * routes load, protected routes redirect to sign-in via the standard
 * auto-guard.
 */
import type { Dispatch } from 'react';
import { setGlobalSessionExpired } from '@api-client/api-client';
import { clearRecoveryRecord } from '@utils/authRecovery';
import { AuthSessionState, type AuthAction } from './authTypes';

export interface TransitionToGuestSessionArgs {
  sessionStateRef: { current: AuthSessionState };
  dispatch: Dispatch<AuthAction>;
}

export function transitionToGuestSession({
  sessionStateRef,
  dispatch,
}: TransitionToGuestSessionArgs): void {
  // clearRecoveryRecord() already calls resetTokenRefreshFailures() internally
  // (authRecovery.ts) so the circuit-breaker counter is zeroed here too —
  // the next outage starts with a clean window.
  clearRecoveryRecord();
  setGlobalSessionExpired(false);
  sessionStateRef.current = AuthSessionState.UNAUTHENTICATED;
  dispatch({ type: 'SET_UNAUTHENTICATED' });
}
